import React, { useEffect, useState, useMemo } from 'react';
import { getService, getClubs, createBooking } from '../api';

const WEEKDAY_FULL = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'];

export default function StepForm({ service, date, time, onSuccess, onBack }) {
    const [serviceDetail, setServiceDetail] = useState(null);
    const [clubs, setClubs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [clubId, setClubId] = useState('');
    const [clubName, setClubName] = useState('');
    const [clubSearch, setClubSearch] = useState('');
    const [showClubDropdown, setShowClubDropdown] = useState(false);
    const [customFields, setCustomFields] = useState({});
    const [errors, setErrors] = useState({});

    useEffect(() => {
        Promise.all([
            getService(service.id),
            service.club_selection_enabled ? getClubs(service.id).catch(() => []) : Promise.resolve([]),
        ]).then(([svc, cls]) => {
            setServiceDetail(svc);
            setClubs(cls);
        })
        .finally(() => setLoading(false));
    }, [service.id, service.club_selection_enabled]);

    const filteredClubs = useMemo(() => {
        if (!clubSearch) return clubs;
        const q = clubSearch.toLowerCase().trim();
        return clubs.filter(c => {
            const haystack = [c.display, c.name, c.city, c.region, c.id]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return haystack.includes(q);
        });
    }, [clubs, clubSearch]);

    const handleClubSelect = (club) => {
        if (club.booked) {
            setErrors(prev => ({ ...prev, club: 'Этот клуб уже записан на этот сервис' }));
            return;
        }
        if (club.truenas) {
            setErrors(prev => ({ ...prev, club: 'Этот клуб недоступен для записи на этот сервис' }));
            return;
        }
        setClubId(club.id);
        setClubName(club.name);
        setClubSearch(club.display || club.name);
        setErrors(prev => ({ ...prev, club: undefined }));
        setShowClubDropdown(false);
    };

    const handleFieldChange = (label, value) => {
        setCustomFields(prev => ({ ...prev, [label]: value }));
    };

    const formatDate = (iso) => {
        const [y, m, d] = iso.split('-');
        return `${d}.${m}.${y}`;
    };

    const handleSubmit = async () => {
        setErrors({});
        setSubmitting(true);

        try {
            const result = await createBooking({
                service_id: service.id,
                date: date,
                time: time,
                club_id: clubId || null,
                club_name: clubName || null,
                custom_fields: Object.keys(customFields).length > 0 ? customFields : null,
            });
            onSuccess(result);
        } catch (err) {
            if (err.status === 400 && err.data) {
                const detail = err.data.detail || err.data;
                if (typeof detail === 'string') {
                    setErrors({ _general: detail });
                } else if (typeof detail === 'object') {
                    setErrors(detail);
                }
            } else {
                setErrors({ _general: 'Произошла ошибка, попробуйте позже' });
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div className="loading"><div className="spinner" /></div>;
    }

    const fields = serviceDetail?.custom_fields || [];

    return (
        <div>
            <button className="btn-back" onClick={onBack}>
                &larr; {service.service_type === 'WEEKLY' ? WEEKDAY_FULL[new Date(date).getDay()] : formatDate(date)}, {time}
            </button>
            <div className="section-title">Заполните данные</div>

            {/* Summary */}
            <div style={{
                background: 'var(--bg-card)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                padding: '12px 16px',
                marginBottom: '16px',
                fontSize: '13px',
                color: 'var(--text-dim)',
            }}>
                <strong style={{ color: 'var(--text)' }}>{service.name}</strong>
                <br />
                {service.service_type === 'WEEKLY' ? WEEKDAY_FULL[new Date(date).getDay()] : formatDate(date)} в {time}
            </div>

            {errors._general && (
                <div style={{
                    background: 'rgba(217,83,79,0.1)',
                    border: '1px solid rgba(217,83,79,0.3)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '10px 14px',
                    marginBottom: '16px',
                    color: 'var(--error)',
                    fontSize: '13px',
                }}>
                    {errors._general}
                </div>
            )}

            {/* Club selector */}
            {service.club_selection_enabled && (
                <div className="form-group">
                    <label className="form-label">
                        Клуб <span className="required">*</span>
                    </label>
                    <div className="club-search">
                        <input
                            type="text"
                            placeholder="Начните вводить название клуба..."
                            value={clubSearch}
                            onChange={(e) => {
                                setClubSearch(e.target.value);
                                setClubId('');
                                setClubName('');
                                setShowClubDropdown(true);
                            }}
                            onFocus={() => setShowClubDropdown(true)}
                            onBlur={() => setTimeout(() => setShowClubDropdown(false), 200)}
                        />
                        {showClubDropdown && filteredClubs.length > 0 && (
                            <div className="club-dropdown">
                                {filteredClubs.map(c => {
                                    const disabled = c.booked || c.truenas;
                                    const badge = c.booked ? 'уже записан' : (c.truenas ? 'TrueNas' : null);
                                    return (
                                        <div
                                            key={c.id}
                                            className={`club-option${disabled ? ' club-option-booked' : ''}`}
                                            onMouseDown={(e) => { e.preventDefault(); handleClubSelect(c); }}
                                        >
                                            <div className="club-option-name">
                                                {c.display || c.name}
                                                {badge && <span className="club-option-badge">{badge}</span>}
                                            </div>
                                            {c.region && <div className="club-option-loc">{c.region}</div>}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                    {errors.club && <div className="form-error">{errors.club}</div>}
                </div>
            )}

            {/* Custom fields */}
            {fields.map(cf => (
                <div className="form-group" key={cf.id}>
                    <label className="form-label">
                        {cf.label} {cf.required && <span className="required">*</span>}
                    </label>
                    {cf.field_type === 'textarea' ? (
                        <textarea
                            className="form-textarea"
                            placeholder={cf.label}
                            value={customFields[cf.label] || ''}
                            onChange={(e) => handleFieldChange(cf.label, e.target.value)}
                        />
                    ) : cf.field_type === 'select' ? (
                        <select
                            className="form-select"
                            value={customFields[cf.label] || ''}
                            onChange={(e) => handleFieldChange(cf.label, e.target.value)}
                        >
                            <option value="">Выберите...</option>
                            {(cf.options || []).map(opt => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </select>
                    ) : (
                        <input
                            type={cf.field_type === 'phone' ? 'tel' : cf.field_type === 'email' ? 'email' : cf.field_type === 'number' ? 'number' : 'text'}
                            className="form-input"
                            placeholder={
                                cf.field_type === 'phone' ? '+7 999 123-45-67'
                                : cf.field_type === 'email' ? 'name@example.com'
                                : cf.label
                            }
                            value={customFields[cf.label] || ''}
                            onChange={(e) => handleFieldChange(cf.label, e.target.value)}
                        />
                    )}
                    {errors.custom_fields && errors.custom_fields[cf.label] && (
                        <div className="form-error">{errors.custom_fields[cf.label]}</div>
                    )}
                </div>
            ))}

            {errors.date && <div className="form-error" style={{ marginBottom: 12 }}>{errors.date}</div>}
            {errors.time && <div className="form-error" style={{ marginBottom: 12 }}>{errors.time}</div>}

            <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={submitting}
            >
                {submitting ? 'Отправка...' : 'Записаться'}
            </button>
        </div>
    );
}
